import type {
	ColumnOrderState,
	SortingState,
	VisibilityState,
} from "@tanstack/react-table";
import { createParser } from "nuqs";
import { DEFAULT_COLUMN_ORDER, DEFAULT_COLUMN_VISIBILITY } from "./columns";

// ── Sorting ──────────────────────────────────────────────────────────────
// Encoded as `field.dir` tokens joined by ",", e.g. "date.desc" or
// "status.asc,date.desc". TanStack only ever drives single-column sort here,
// but the multi-token form costs nothing and future-proofs the param.

export const DEFAULT_SORTING: SortingState = [{ id: "date", desc: true }];

function serializeSorting(sorting: SortingState): string {
	return sorting.map((s) => `${s.id}.${s.desc ? "desc" : "asc"}`).join(",");
}

export const sortingParser = createParser<SortingState>({
	parse(value) {
		const out: SortingState = [];
		for (const token of value.split(",")) {
			const dot = token.lastIndexOf(".");
			if (dot <= 0) continue;
			const id = token.slice(0, dot);
			const dir = token.slice(dot + 1);
			if (dir !== "asc" && dir !== "desc") continue;
			out.push({ id, desc: dir === "desc" });
		}
		return out;
	},
	serialize: serializeSorting,
	eq: (a, b) => serializeSorting(a) === serializeSorting(b),
});

// ── Column visibility ────────────────────────────────────────────────────
// Encoded as a signed diff against DEFAULT_COLUMN_VISIBILITY: a column hidden
// relative to default is "-id", a column shown relative to default is "+id".
// Columns left at their default emit nothing, so a pristine table clears the
// param entirely. On parse we rebuild the full VisibilityState from the
// default plus those overrides, so the table + Options menu see exactly the
// shape they'd have from useState.

const isShown = (v: VisibilityState, key: string): boolean => v[key] !== false;

/**
 * Build a visibility parser bound to a default map. Each mode (normal,
 * auto-combine) has its own defaults, so the signed-diff encoding is computed
 * against the matching baseline.
 */
export function makeVisibilityParser(defaults: VisibilityState) {
	function serialize(visibility: VisibilityState): string {
		const keys = new Set([
			...Object.keys(defaults),
			...Object.keys(visibility),
		]);
		const tokens: string[] = [];
		for (const key of keys) {
			const shown = isShown(visibility, key);
			const defaultShown = isShown(defaults, key);
			if (shown === defaultShown) continue;
			tokens.push(`${shown ? "+" : "-"}${key}`);
		}
		return tokens.join(",");
	}
	return createParser<VisibilityState>({
		parse(value) {
			const overrides: VisibilityState = {};
			for (const token of value.split(",")) {
				const sign = token[0];
				const key = token.slice(1);
				if (!key) continue;
				if (sign === "-") overrides[key] = false;
				else if (sign === "+") overrides[key] = true;
			}
			return { ...defaults, ...overrides };
		},
		serialize,
		eq: (a, b) => serialize(a) === serialize(b),
	});
}

export const visibilityParser = makeVisibilityParser(DEFAULT_COLUMN_VISIBILITY);

// ── Column order ─────────────────────────────────────────────────────────
// Encoded as the ordered list of user-reorderable display columns. Non-display
// implementation columns (selection + filter-only fields) are added by the
// view so they stay pinned/hidden while the visible table columns move.

/** Build a column-order parser bound to a mode's default order. */
export function makeColumnOrderParser(defaultOrder: ColumnOrderState) {
	function normalize(order: ColumnOrderState): ColumnOrderState {
		const allowed = new Set(defaultOrder);
		const seen = new Set<string>();
		const out: string[] = [];
		for (const id of order) {
			if (!allowed.has(id) || seen.has(id)) continue;
			seen.add(id);
			out.push(id);
		}
		for (const id of defaultOrder) {
			if (!seen.has(id)) out.push(id);
		}
		return out;
	}
	function serialize(order: ColumnOrderState): string {
		return normalize(order).join(",");
	}
	return createParser<ColumnOrderState>({
		parse(value) {
			return normalize(value.split(",").filter(Boolean));
		},
		serialize,
		eq: (a, b) => serialize(a) === serialize(b),
	});
}

export const columnOrderParser = makeColumnOrderParser(DEFAULT_COLUMN_ORDER);
